import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import numeral from 'numeral';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@material-ui/core/styles';

import { showSuccessSnackbar } from 'modules/snackbar';
import { makeGraph, makeLabel, xAxisTick } from 'lib/commonLib';
import { paramConverter } from 'modules/common';
import { graphChangeLoading } from 'modules/filter';
import { setPortal } from 'modules/portal';

import CurrentStatus from 'components/atoms/CurrentStatus';
import OutlinedButton from 'components/atoms/OutlinedButton';
import LabelNvalue from 'components/atoms/LabelNvalue';
import ChartButtonWrap from 'components/molecules/ChartButtonWrap';
import ContentsWrap from 'components/molecules/ContentsWrap';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import SwitchingArea from 'components/organisms/SwitchingArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';

const BarGraph = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  const {
    params,
    graphList = [],
    infoMessage = t('까지의 통계 그래프입니다.', {
      date: t('term', {
        startDate: moment(params.sDate).format('YYYY-MM-DD'),
        endDate: moment(params.eDate).format('YYYY-MM-DD'),
      }),
    }),
    title,
    labelType = 'MMDD',
    sumCount = [],
    disabled = [],
    dataReqPromise = (f) => f,
    titleLink,
    useChartButton = true,
    useStackId = true,
    subTitle,
  } = props;

  const [curGraphComponent, setCurGraphComponent] = useState(false);
  const [graphIndex, setGraphIndex] = useState(0);
  const [barData, setBarData] = useState([]);
  const [status, setStatus] = useState('loading');
  const usePrevious = (value) => {
    const ref = useRef();

    useEffect(() => {
      ref.current = value;
    }, [value]);

    return ref.current;
  };
  const prevParams = usePrevious(params);

  useEffect(() => {
    function graphChangeClt(state) {
      dispatch(graphChangeLoading(state));
    }
    if (status === 'loading') {
      graphChangeClt(true);
    } else {
      graphChangeClt(false);
    }
  }, [status, dispatch]);

  useEffect(() => {
    if (status !== 'loading') {
      handleChangeGraph(graphList[0], 0);
    }
    setGraphIndex(0);
    // eslint-disable-next-line
  }, [barData, status]);

  useEffect(() => {
    if (JSON.stringify(params) !== JSON.stringify(prevParams)) {
      setStatus('loading');
      dataReqPromise(paramConverter(params))
        .then((response) => {
          const { data } = response.data;
          if (Object.keys(data).length !== 0 || data.length > 0) {
            setBarData(data);
            setStatus('');
          } else {
            setBarData({});
            setStatus('noData');
          }
        })
        .catch((error) => {
          setStatus('error');
          handleClickSnackbar({
            message: error,
            option: {
              type: 'error',
            },
          });
        });
    }

    // eslint-disable-next-line
  }, [params]);

  const makePopLabel = (date) => {
    const dateObj = moment(date, 'YYYYMMDD');
    const day = dateObj.day();
    const dayStr = makeLabel(false, labelType)(date);
    const length = String(date).length;
    let color = '';

    if (length !== 4 && length !== 6) {
      if (day === 0) {
        color = theme.palette.common.red;
      } else if (day === 6) {
        color = theme.palette.common.blue;
      }
    }

    return <span style={{ color: color }}>{dayStr}</span>;
  };

  const handleChangeGraph = (
    {
      title,
      data,
      yAxisData,
      customizeData: customize,
      isPercent = false,
      referenceLine,
    },
    index = 0
  ) => {
    let result;
    setGraphIndex(index);

    if (status === '') {
      const {
        graphData,
        dataKeys,
        customizeData,
        keyLabel,
        stackId,
        referenceData,
      } = makeGraph({ data, customize, primitiveData: barData, referenceLine });
      result = (
        <>
          {!!sumCount.data &&
            !!sumCount.data[index] &&
            !!sumCount.label[index] && (
              <LabelNvalue
                label={sumCount.label[index]}
                value={
                  <>
                    {numeral(sumCount.data[index]).format()}
                    {'원'}
                  </>
                }
              />
            )}

          <MultiplexChart
            data={graphData}
            xAxisTick={(e) => xAxisTick(e, labelType)}
            makeLabel={(date) => makePopLabel(date)}
            dataKeys={dataKeys}
            customizeData={customizeData}
            keyLabel={keyLabel}
            stackId={
              useStackId
                ? stackId.length > 1
                  ? stackId.join('$')
                  : stackId
                : null
            }
            yAxisData={yAxisData}
            legend
            title={title}
            isPercent={isPercent}
            key={title}
            referenceLine={referenceData}
          />
        </>
      );
      setCurGraphComponent(result);
    }
  };

  /**
   * 스낵바 리덕스 값을 변경해주는 함수
   * @type {(...args: any[]) => any}
   */
  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  return (
    <>
      <SwitchingArea
        title={
          <>
            {!!titleLink && titleLink}
            <InfoTitleArea
              title={title}
              tooltipTitle={infoMessage}
              iconType="fainfoCircle"
              iconColor="graph"
            />
          </>
        }
      >
        {status !== '' ? (
          <CurrentStatus type={status} />
        ) : (
          <>
            {!!subTitle && (
              <p style={{ marginTop: 0, marginBottom: 10 }}>{subTitle}</p>
            )}
            {useChartButton && (
              <ChartButtonWrap>
                {graphList.map((item, index) => {
                  let useLink = false;
                  if (!!graphList[index]['customizeData']) {
                    if (!!graphList[index]['customizeData']['link']) {
                      useLink = true;
                    }
                  }
                  return (
                    !!item.title && (
                      <OutlinedButton
                        key={index}
                        onClick={() =>
                          useLink
                            ? handlePortalUrl(
                                graphList[index]['customizeData']['link']
                              )
                            : handleChangeGraph(item, index)
                        }
                        selected={graphIndex === index}
                        disabled={disabled.includes(index)}
                      >
                        {item.title}
                      </OutlinedButton>
                    )
                  );
                })}
              </ChartButtonWrap>
            )}
            <ContentsWrap>{curGraphComponent}</ContentsWrap>
          </>
        )}
      </SwitchingArea>
    </>
  );
};

export default BarGraph;
